// Copied from delve-portal: keep in step. The workshop's texts: one i18next instance, catalogs bundled per language
// (locales/<lang>/<namespace>.json), one namespace per feature. React pages read it through react-i18next (App's
// provider); plain modules (zod schemas) call `t` directly. English only for now: a language is a folder here and an
// entry in LANGUAGES.
import i18n from "i18next";
import { en } from "./locales/en";

export const LANGUAGES = ["en"] as const;
export type Language = (typeof LANGUAGES)[number];
export const DEFAULT_LANGUAGE: Language = "en";

export const resources = { en } as const;

/** The visitor's first browser language we have ("en-GB" picks "en"), else English. */
export function pickLanguage(wanted: readonly string[] = typeof navigator === "undefined" ? [] : navigator.languages ?? []): Language {
  for (const tag of wanted) {
    const base = tag.toLowerCase().split("-")[0];
    const hit = LANGUAGES.find((l) => l === base);
    if (hit) return hit;
  }
  return DEFAULT_LANGUAGE;
}

void i18n.init({
  resources,
  lng: pickLanguage(),
  fallbackLng: DEFAULT_LANGUAGE,
  supportedLngs: LANGUAGES,
  ns: Object.keys(en),
  defaultNS: "common",
  // Bundled catalogs: ready synchronously, so the first render already has its texts.
  initAsync: false,
  // React escapes, and the game page only sets textContent.
  interpolation: { escapeValue: false },
  returnNull: false,
});

const applyLang = (lng: string) => { if (typeof document !== "undefined") document.documentElement.lang = lng; };
applyLang(i18n.language);
i18n.on("languageChanged", applyLang);

export { i18n };
export const t = i18n.t.bind(i18n);

/** The locale Intl formats with (dates, numbers): the browser's own region when it speaks the page's language
 *  ("en-GB" dates on the English site), else the page's language. */
export function locale(): string {
  const lang = i18n.resolvedLanguage ?? DEFAULT_LANGUAGE;
  const browser = typeof navigator === "undefined" ? [] : navigator.languages ?? [];
  return browser.find((tag) => tag.toLowerCase().split("-")[0] === lang) ?? lang;
}

/** A label for a value that comes from the server or the game (a mode, an outcome, a rejection reason): its entry
 *  under `prefix` when the catalog has one, else the raw value, so a new value still shows. */
export function labelOf(prefix: string, value: string): string {
  const key = `${prefix}.${value}`;
  return i18n.exists(key) ? (i18n.t as (k: string) => string)(key) : value;
}
